import { find, length, map, path, pathOr, reduce } from 'ramda';
import cookie from 'cookie-cutter';

import { denormalisedResponseEntities } from '../util/data';
import { storableError } from '../util/errors';
import * as log from '../util/log';
import { loginToMedianOneSignal } from '../util/medianOneSignal';
import { updateProfile } from '../ducks/userSettings.duck';
import { authInfo } from './Auth.duck';
import { stripeAccountCreateSuccess } from './stripeConnectAccount.duck';
import { util as sdkUtil } from '../util/sdkLoader';
import { BOOKING_UPCOMING_TRANSITIONS, BOOKING_PENDING_TRANSITIONS } from '../util/transactions';
import { updateOwnListingsCities } from '../util/swimmyOnLoginActions';
import {
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
} from '../constants/listing';
import { isNilOrEmpty } from '../util/validators';
import { moment } from '../util/dates';
import { addMarketplaceEntities } from './marketplaceData.duck';
import {
  DATADOG_RUM_ELIGIBILITY_REASONS,
  DATADOG_RUM_SEGMENTS,
} from '../analytics/datadogEligibility';

// ================ Action types ================ //

export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR = 'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const FETCH_CURRENT_USER_HAS_UPCOMING_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_UPCOMING_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_UPCOMING_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_UPCOMING_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_UPCOMING_ORDERS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_UPCOMING_ORDERS_ERROR';

export const FETCH_CURRENT_USER_HAS_PENDING_SALES_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_PENDING_SALES_REQUEST';
export const FETCH_CURRENT_USER_HAS_PENDING_SALES_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_PENDING_SALES_SUCCESS';
export const FETCH_CURRENT_USER_HAS_PENDING_SALES_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_PENDING_SALES_ERROR';

export const SEND_VERIFICATION_EMAIL_REQUEST = 'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS = 'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR = 'app/user/SEND_VERIFICATION_EMAIL_ERROR';

export const SET_CAN_SHOW_REQUIREMENT_FIELDS = 'app/user/SET_CAN_SHOW_REQUIREMENT_FIELDS';

export const CART_FETCH_REQUESTED = 'CART_FETCH_REQUESTED';
export const CART_FETCH_SUCCESS = 'CART_FETCH_SUCCESS';
export const CART_FETCH_ERROR = 'CART_FETCH_ERROR';

export const CART_ADDING_REQUESTED = 'CART_ADDING_REQUESTED';
export const CART_ADDING_SUCCESS = 'CART_ADDING_SUCCESS';
export const CART_ADDING_RE_INIT_SUCCESS = 'CART_ADDING_RE_INIT_SUCCESS';
export const CART_DELETING_REQUESTED = 'CART_DELETING_REQUESTED';
export const CART_UPDATE_SUCCESS = 'CART_UPDATE_SUCCESS';
export const CART_UPDATE_ERROR = 'CART_UPDATE_ERROR';

// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

const initialState = {
  currentUser: null,
  currentUserLoading: false,
  currentUserShowError: null,
  currentUserHasListings: false,
  currentUserHasPublishedListings: false,
  currentUserHasListingsError: null,
  currentUserHasOrders: null, // This is not fetched unless unverified emails exist
  currentUserHasOrdersError: null,
  currentUserHasPendingSales: false,
  currentUserHasPendingSalesError: null,
  currentUserHasUpcomingOrders: false,
  currentUserHasUpcomingOrdersError: null,
  currentUserListingRefs: [],
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
  isLoadingCart: false,
  isCartAddingSuccess: false,
  myCart: [],
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CART_ADDING_RE_INIT_SUCCESS:
      return {
        ...state,
        isLoadingCart: false,
        isCartAddingSuccess: false,
      };
    case CART_ADDING_REQUESTED:
    case CART_DELETING_REQUESTED:
    case CART_FETCH_REQUESTED:
      return {
        ...state,
        isLoadingCart: true,
        error: null,
        result: null,
        isCartAddingSuccess: false,
      };
    case CART_ADDING_SUCCESS:
      return {
        ...state,
        isLoadingCart: false,
        isCartAddingSuccess: true,
      };
    case CART_UPDATE_SUCCESS:
      return {
        ...state,
        isLoadingCart: false,
        error: null,
        // result: success,
        ...(payload.myCart ? { myCart: payload.myCart } : {}),
      };
    case CART_FETCH_SUCCESS:
      return {
        ...state,
        isLoadingCart: false,
        error: null,
        // result: success,
        ...(payload.myCart ? { myCart: payload.myCart } : {}),
      };
    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserLoading: true, currentUserShowError: null };
    case CURRENT_USER_SHOW_SUCCESS:
      return {
        ...state,
        currentUser: mergeCurrentUser(state.currentUser, payload),
        currentUserLoading: false,
      };
    case CURRENT_USER_SHOW_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        currentUserShowError: payload,
        currentUserLoading: false,
      };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserHasPublishedListings: false,
        currentUserHasListingsError: null,
        currentUserHasPendingSales: false,
        currentUserHasPendingSalesError: null,
        currentUserHasUpcomingOrders: false,
        currentUserHasUpcomingOrdersError: null,
      };

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return {
        ...state,
        currentUserHasListings: payload.hasListings,
        currentUserHasPublishedListings: payload.hasPublishedListings,
        currentUserListingRefs: payload.listingRefs,
      };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return { ...state, currentUserHasOrders: payload.hasOrders };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasOrdersError: payload };

    case FETCH_CURRENT_USER_HAS_UPCOMING_ORDERS_REQUEST:
      return { ...state, currentUserHasUpcomingOrdersError: null };
    case FETCH_CURRENT_USER_HAS_UPCOMING_ORDERS_SUCCESS:
      return { ...state, currentUserHasUpcomingOrders: payload.hasUpcomingOrders };
    case FETCH_CURRENT_USER_HAS_UPCOMING_ORDERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasUpcomingOrdersError: payload };

    case FETCH_CURRENT_USER_HAS_PENDING_SALES_REQUEST:
      return { ...state, currentUserHasPendingSalesError: null };
    case FETCH_CURRENT_USER_HAS_PENDING_SALES_SUCCESS:
      return { ...state, currentUserHasPendingSales: payload.hasPendingSales };
    case FETCH_CURRENT_USER_HAS_PENDING_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasPendingSalesError: payload };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };

    case SET_CAN_SHOW_REQUIREMENT_FIELDS:
      return {
        ...state,
        ...payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = (state) => {
  const { user } = state;
  return (
    user.currentUserShowError || user.currentUserHasListingsError || user.currentUserHasOrdersError
  );
};

export const verificationSendingInProgress = (state) => {
  return state.user.sendVerificationEmailInProgress;
};

// ================ Action creators ================ //

export const currentUserShowRequest = () => ({ type: CURRENT_USER_SHOW_REQUEST });

export const currentUserShowSuccess = (user) => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = (e) => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = (
  listingRefs,
  hasListings,
  hasPublishedListings
) => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload: { hasListings, hasPublishedListings, listingRefs },
});

const fetchCurrentUserHasListingsError = (e) => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});

export const fetchCurrentUserHasOrdersSuccess = (hasOrders) => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: { hasOrders },
});

const fetchCurrentUserHasOrdersError = (e) => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasUpcomingOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_UPCOMING_ORDERS_REQUEST,
});

export const fetchCurrentUserHasUpcomingOrdersSuccess = (hasUpcomingOrders) => ({
  type: FETCH_CURRENT_USER_HAS_UPCOMING_ORDERS_SUCCESS,
  payload: { hasUpcomingOrders },
});

const fetchCurrentUserHasUpcomingOrdersError = (e) => ({
  type: FETCH_CURRENT_USER_HAS_UPCOMING_ORDERS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasPendingSalesRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_PENDING_SALES_REQUEST,
});

export const fetchCurrentUserHasPendingSalesSuccess = (hasPendingSales) => ({
  type: FETCH_CURRENT_USER_HAS_PENDING_SALES_SUCCESS,
  payload: { hasPendingSales },
});

const fetchCurrentUserHasPendingSalesError = (e) => ({
  type: FETCH_CURRENT_USER_HAS_PENDING_SALES_ERROR,
  error: true,
  payload: e,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});

export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});

export const sendVerificationEmailError = (e) => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: e,
});

export const setCanShowRequirementFields = (requirementsToShow) => ({
  type: SET_CAN_SHOW_REQUIREMENT_FIELDS,
  payload: { requirementsToShow },
});

export const addToCartReInit = () => ({
  type: CART_ADDING_RE_INIT_SUCCESS,
});

export const fetchMyCartRequest = () => ({
  type: CART_FETCH_REQUESTED,
});

export const addToCartRequest = (listingId, params) => ({
  type: CART_ADDING_REQUESTED,
  payload: {
    listingId,
    params,
  },
});

export const addToCartSuccess = () => ({
  type: CART_ADDING_SUCCESS,
});

// export const addToCartError = e => ({
//   type: CART_ADDING_ERROR,
//   error: true,
//   payload: e,
// });

export const updateCartError = () => ({
  type: CART_UPDATE_ERROR,
});

export const removeFromCartRequest = (listingIds) => ({
  type: CART_DELETING_REQUESTED,
  payload: {
    listingIds,
  },
});

// ================ Thunks ================ //

const canShowRequirementsFields = (requirements) => (dispatch, getState, sdk) => {
  const canShowRequirementsFieldsPayload = reduce(
    (acc, requirement) => {
      switch (requirement) {
        case 'individual.verification.additional_document':
          return { ...acc, canShowCNIField: true, canShowProofOfAddress: true };
        case 'individual.verification.document':
          return { ...acc, canShowCNIField: true };
        default:
          return acc;
      }
    },
    {},
    requirements
  );
  return dispatch(setCanShowRequirementFields(canShowRequirementsFieldsPayload));
};

const activeStripeCapabilities = () => async (dispatch, getState, sdk) => {
  try {
    const res = await sdk.stripeAccount.fetch();
    const requirements = pathOr(
      [],
      ['data', 'data', 'attributes', 'stripeAccountData', 'requirements', 'currently_due'],
      res
    );
    dispatch(canShowRequirementsFields(requirements));

    const { card_payments, transfers } = pathOr(
      {},
      ['data', 'data', 'attributes', 'stripeAccountData', 'capabilities'],
      res
    );
    const inactiveStripe = 'inactive';
    if (card_payments !== inactiveStripe && transfers !== inactiveStripe && transfers) return;

    const requestedCapabilities = ['card_payments', 'transfers'];
    const updateResult = await sdk.stripeAccount.update(
      {
        requestedCapabilities,
      },
      {
        expand: true,
      }
    );
    const requirementsAfterUpdate = pathOr(
      [],
      ['data', 'data', 'attributes', 'stripeAccountData', 'requirements', 'currently_due'],
      updateResult
    );
    dispatch(canShowRequirementsFields(requirementsAfterUpdate));
  } catch (e) {}
};

export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(fetchCurrentUserHasListingsSuccess([], false, false));
    return Promise.resolve(null);
  }

  const params = {
    // Since we are only interested in if the user has
    // listings, we only need at most one result.
    page: 1,
    'fields.ownListing': ['state', 'publicData', 'privateData'],
  };

  return sdk.ownListings
    .query(params)
    .then((response) => {
      const listingRefs = map(
        (entity) => ({ id: entity.id, type: entity.type }),
        response.data.data
      );
      const hasListings = length(listingRefs) > 0;
      const hasPublishedListings =
        hasListings &&
        find((listing) => listing.attributes.state === LISTING_STATE_PUBLISHED, response.data.data);
      dispatch(
        fetchCurrentUserHasListingsSuccess(listingRefs, !!hasListings, !!hasPublishedListings)
      );
      dispatch(applyPendingDatadogRumCustomerLeadEligibility());
      dispatch(addMarketplaceEntities(response));
    })
    .catch((e) => dispatch(fetchCurrentUserHasListingsError(storableError(e))));
};

export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    page: 1,
    per_page: 1,
    'fields.transaction': [],
  };

  return sdk.transactions
    .query(params)
    .then((response) => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasOrdersSuccess(!!hasOrders));
    })
    .catch((e) => dispatch(fetchCurrentUserHasOrdersError(storableError(e))));
};

export const fetchCurrentUserHasUpcomingOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasUpcomingOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasUpcomingOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    lastTransitions: BOOKING_UPCOMING_TRANSITIONS,
    page: 1,
    per_page: 1,
    'fields.transaction': [],
  };

  return sdk.transactions
    .query(params)
    .then((response) => {
      const hasUpcomingOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasUpcomingOrdersSuccess(!!hasUpcomingOrders));
    })
    .catch((e) => dispatch(fetchCurrentUserHasUpcomingOrdersError(storableError(e))));
};

const getAffiliateId = () => {
  try {
    const fromAffiliateId =
      typeof window === 'undefined' ? undefined : window.AeTracker?.affiliateID;
    return fromAffiliateId;
  } catch (e) {
    return;
  }
};

const handleAffiliationAffilae = async (dispatch, user) => {
  const currentUserFromAffiliateId = pathOr(
    null,
    ['currentUser', 'attributes', 'profile', 'privateData', 'fromAffiliateId'],
    user
  );
  if (currentUserFromAffiliateId) return;

  const fromAffiliateId = getAffiliateId();
  console.log('swimmy ae', fromAffiliateId);
  if (!fromAffiliateId) return;

  dispatch(
    updateProfile({
      privateData: {
        fromAffiliateId: fromAffiliateId ?? undefined,
      },
    })
  );
  identifyBrevoUser(user?.currentUser);
};

export const fetchCurrentUserHasPendingSales = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasPendingSalesRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasPendingSalesSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'sale',
    lastTransitions: BOOKING_PENDING_TRANSITIONS,
    page: 1,
    per_page: 1,
    'fields.transaction': [],
  };

  return sdk.transactions
    .query(params)
    .then((response) => {
      const hasPendingSales = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasPendingSalesSuccess(!!hasPendingSales));
    })
    .catch((e) => dispatch(fetchCurrentUserHasPendingSalesError(storableError(e))));
};

const trackAffiliationBrevo = (email, affiliateId) => {
  try {
    if (!email || !affiliateId) return;

    window.sendinblue.track(
      'AFFILIATION_DETECTED',
      {
        email,
        AFFILIATE_ID: affiliateId,
      },
      {
        email,
        AFFILIATE_ID: affiliateId,
      }
    );
  } catch (e) {
    console.log('trackAffiliationBrevo err', e);
  }
};

const formatDateToYYYYMMDD = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');

  return `${day}-${month}-${year}`;
};

const identifyBrevoUser = (user) => {
  try {
    if (!user) return;

    const userId = pathOr('', ['id', 'uuid'], user);
    const email = pathOr('', ['attributes', 'email'], user);
    const emailVerified = pathOr(false, ['attributes', 'emailVerified'], user);
    const createdAt = pathOr('', ['createdAt'], user);
    const firstName = pathOr('', ['attributes', 'profile', 'firstName'], user);
    const lastName = pathOr('', ['attributes', 'profile', 'lastName'], user);
    const country = pathOr('fr', ['attributes', 'profile', 'privateData', 'country'], user);
    const language = pathOr('fr', ['attributes', 'profile', 'privateData', 'language'], user);
    const hasAcceptedNewsletter = pathOr(
      false,
      ['attributes', 'profile', 'protectedData', 'hasAcceptedNewsletter'],
      user
    );
    const phoneNumber = pathOr('', ['attributes', 'profile', 'protectedData', 'phoneNumber'], user);
    const birthdayDate = pathOr(
      '',
      ['attributes', 'profile', 'protectedData', 'birthdayDate'],
      user
    );
    const swimPlusLevel = pathOr(0, ['attributes', 'profile', 'metadata', 'swimPlusLevel'], user);
    const affiliateId = getAffiliateId();
    const payload = {
      EMAIL: email,
      USER_ID: userId,
      EMAIL_VERIFIED: emailVerified,
      CREATE_DATE: formatDateToYYYYMMDD(createdAt),
      PRENOM: firstName,
      NOM: lastName,
      COUNTRY: country,
      LANGUAGE: language,
      OPTIN_NL: hasAcceptedNewsletter,
      SMS: phoneNumber,
      SWIM_PLUS_LEVEL: swimPlusLevel,
      BIRTH_DATE: birthdayDate,
      AFFILIATE_ID: affiliateId,
    };
    window.sendinblue.identify(email, payload);
    trackAffiliationBrevo(email, affiliateId);
  } catch (e) {
    console.log('identifyBrevoUser err', e);
  }
};

export const handleAffiliation = () => (dispatch, getState, sdk) => {
  try {
    const user = getState().user;
    handleAffiliationAffilae(dispatch, user);
  } catch (e) {}
};

export const fetchCurrentUser =
  (params = null) =>
  (dispatch, getState, sdk) => {
    dispatch(currentUserShowRequest());
    const { isAuthenticated } = getState().Auth;

    if (!isAuthenticated) {
      // Make sure current user is null
      dispatch(currentUserShowSuccess(null));
      return Promise.resolve({});
    }

    const parameters = params || {
      include: ['profileImage', 'stripeAccount'],
      'fields.image': [
        'variants.square-small',
        'variants.square-small2x',
        'variants.square-xsmall',
        'variants.square-xsmall2x',
      ],
      'imageVariant.square-xsmall': sdkUtil.objectQueryString({
        w: 40,
        h: 40,
        fit: 'crop',
      }),
      'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
        w: 80,
        h: 80,
        fit: 'crop',
      }),
    };
    dispatch(activeStripeCapabilities());

    return sdk.currentUser
      .show(parameters)
      .then((response) => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.show response');
        }
        const currentUser = entities[0];

        // Save stripeAccount to store.stripe.stripeAccount if it exists
        if (currentUser.stripeAccount) {
          dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
        }

        // set current user id to the logger
        log.setUserId(currentUser.id.uuid);
        dispatch(currentUserShowSuccess(currentUser));
        loginToMedianOneSignal(currentUser);
        return currentUser;
      })
      .then((currentUser) => {
        dispatch(fetchCurrentUserHasListings());
        dispatch(fetchCurrentUserHasUpcomingOrders());
        dispatch(fetchCurrentUserHasPendingSales());
        updateOwnListingsReviewsInfo(sdk);
        updateOwnListingsCities(sdk);

        if (!currentUser.attributes.emailVerified) {
          dispatch(fetchCurrentUserHasOrders());
        } else if (!path(['attributes', 'profile', 'publicData', 'emailVerified'], currentUser)) {
          const emailStatus = {
            publicData: {
              emailVerified: true,
            },
          };
          dispatch(updateProfile(emailStatus));
        }

        // Make sure auth info is up to date
        dispatch(authInfo());
        const user = getState().user;
        handleAffiliationAffilae(dispatch, user);
        identifyBrevoUser(user?.currentUser);
        return currentUser;
      })
      .catch((e) => {
        // Make sure auth info is up to date
        dispatch(authInfo());
        log.error(e, 'fetch-current-user-failed');
        dispatch(currentUserShowError(storableError(e)));
      });
  };

export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(new Error('Verification email sending already in progress'));
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch((e) => dispatch(sendVerificationEmailError(storableError(e))));
};

export const getOwnListingIds = async (sdk) => {
  try {
    const response = await sdk.ownListings.query({});
    const listings = pathOr([], ['data', 'data'], response);
    const listingIds = listings.map((listing) => listing.id);

    return listingIds;
  } catch (e) {
    return null;
  }
};

export const getReviewsFromListingId = async (sdk, listingId) => {
  try {
    const params = {
      listingId: listingId,
      state: 'public',
    };
    const response = await sdk.reviews.query(params);
    const reviews = pathOr([], ['data', 'data'], response);

    return reviews;
  } catch (e) {
    return null;
  }
};

const calculateReviewsAverageRating = (reviews) => {
  const reviewsRating = reviews.map((review) => review.attributes.rating);
  const averageRating = (reviewsRatingArray) =>
    reviewsRatingArray.reduce((a, b) => a + b) / reviewsRatingArray.length;
  return Math.round(averageRating(reviewsRating) * 100) / 100;
};

export const updateOwnListingsReviewsInfo = async (sdk) => {
  try {
    const listingIds = await getOwnListingIds(sdk);

    for (const listingId of listingIds) {
      const reviews = await getReviewsFromListingId(sdk, listingId);
      const totalReviews = length(reviews);
      const averageRating = totalReviews > 0 ? calculateReviewsAverageRating(reviews) : 0;

      const params = {
        id: listingId,
        publicData: {
          reviewsInfo: {
            averageRating: averageRating,
            totalReviews: totalReviews,
          },
        },
      };
      await sdk.ownListings.update(params);
    }
  } catch (e) {
    return null;
  }
};

export const getUserToken = () => {
  const clientId = process.env.REACT_APP_SHARETRIBE_SDK_CLIENT_ID;
  const namespace = 'st';
  const key = `${namespace}-${clientId}-token`;
  const tokenRaw = cookie.get(key) || '{}';
  const tokenObj = JSON.parse(tokenRaw);
  if (!tokenObj.access_token) return null;

  return tokenObj;
};

const PENDING_DATADOG_RUM_CUSTOMER_LEAD_REASON = 'pendingDatadogRumCustomerLeadReason';

const storePendingDatadogRumCustomerLeadReason = (reason) => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }
    window.sessionStorage.setItem(PENDING_DATADOG_RUM_CUSTOMER_LEAD_REASON, reason);
  } catch (e) {}
};

const consumePendingDatadogRumCustomerLeadReason = () => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return null;
    }
    const reason = window.sessionStorage.getItem(PENDING_DATADOG_RUM_CUSTOMER_LEAD_REASON);
    window.sessionStorage.removeItem(PENDING_DATADOG_RUM_CUSTOMER_LEAD_REASON);
    return reason;
  } catch (e) {
    return null;
  }
};

const resolveDatadogRumSegmentId = (state, requestedSegmentId) => {
  const currentUserHasPublishedListings = pathOr(
    false,
    ['user', 'currentUserHasPublishedListings'],
    state
  );
  const currentSegmentId = pathOr(
    null,
    ['user', 'currentUser', 'attributes', 'profile', 'privateData', 'datadogRumSegmentId'],
    state
  );

  if (
    currentUserHasPublishedListings ||
    currentSegmentId === DATADOG_RUM_SEGMENTS.OWNER ||
    requestedSegmentId === DATADOG_RUM_SEGMENTS.OWNER
  ) {
    return DATADOG_RUM_SEGMENTS.OWNER;
  }

  return DATADOG_RUM_SEGMENTS.CUSTOMER_LEAD;
};

export const markDatadogRumEligible =
  (reason, requestedSegmentId = DATADOG_RUM_SEGMENTS.CUSTOMER_LEAD) =>
  (dispatch, getState) => {
    try {
      const state = getState();
      const currentUser = pathOr(null, ['user', 'currentUser'], state);

      if (!currentUser) {
        if (requestedSegmentId === DATADOG_RUM_SEGMENTS.CUSTOMER_LEAD) {
          storePendingDatadogRumCustomerLeadReason(reason);
        }
        return Promise.resolve(null);
      }

      const segmentId = resolveDatadogRumSegmentId(state, requestedSegmentId);
      const eligibilityReason =
        segmentId === DATADOG_RUM_SEGMENTS.OWNER &&
        requestedSegmentId === DATADOG_RUM_SEGMENTS.CUSTOMER_LEAD
          ? DATADOG_RUM_ELIGIBILITY_REASONS.PROVIDER_PUBLISHED_LISTING
          : reason;
      const datadogRumEnabled = pathOr(
        false,
        ['attributes', 'profile', 'privateData', 'datadogRumEnabled'],
        currentUser
      );
      const currentSegmentId = pathOr(
        null,
        ['attributes', 'profile', 'privateData', 'datadogRumSegmentId'],
        currentUser
      );

      if (datadogRumEnabled && currentSegmentId === segmentId) {
        return Promise.resolve(null);
      }

      return dispatch(
        updateProfile({
          privateData: {
            datadogRumEnabled: true,
            datadogRumSegmentId: segmentId,
            datadogRumEligibilityReason: eligibilityReason,
            datadogRumEligibleAt: moment().toISOString(),
          },
        })
      );
    } catch (e) {
      log.error(e, 'datadog-rum-eligibility-update-failed', { reason });
      return Promise.resolve(null);
    }
  };

export const markDatadogRumProviderEligible = () =>
  markDatadogRumEligible(
    DATADOG_RUM_ELIGIBILITY_REASONS.PROVIDER_PUBLISHED_LISTING,
    DATADOG_RUM_SEGMENTS.OWNER
  );

export const markDatadogRumCustomerLeadAddToCartEligible = () =>
  markDatadogRumEligible(
    DATADOG_RUM_ELIGIBILITY_REASONS.CUSTOMER_LEAD_ADD_TO_CART,
    DATADOG_RUM_SEGMENTS.CUSTOMER_LEAD
  );

export const markDatadogRumCustomerLeadReserveClickEligible = () =>
  markDatadogRumEligible(
    DATADOG_RUM_ELIGIBILITY_REASONS.CUSTOMER_LEAD_RESERVE_CLICK,
    DATADOG_RUM_SEGMENTS.CUSTOMER_LEAD
  );

const applyPendingDatadogRumCustomerLeadEligibility = () => (dispatch) => {
  const pendingReason = consumePendingDatadogRumCustomerLeadReason();
  if (!pendingReason) {
    return Promise.resolve(null);
  }

  return dispatch(markDatadogRumEligible(pendingReason, DATADOG_RUM_SEGMENTS.CUSTOMER_LEAD));
};

export const addToCart =
  (listingId, params = {}) =>
  async (dispatch, getState, sdk) => {
    try {
      dispatch(addToCartRequest());
      const { bookingSearchId, bookingParams, isSameSearchBookingParamsAsCurrentBookingParams } =
        params;
      const cartBookingSearchId = pathOr(
        null,
        ['currentUser', 'attributes', 'profile', 'privateData', 'cartBookingSearchId'],
        getState().user
      );
      const cart = pathOr(
        [],
        ['currentUser', 'attributes', 'profile', 'publicData', 'addToCart'],
        getState().user
      );
      const getMergedCart = () => {
        if (
          isNilOrEmpty(cart) ||
          !isSameSearchBookingParamsAsCurrentBookingParams ||
          bookingSearchId !== cartBookingSearchId
        )
          return [listingId];
        if (find((lid) => lid === listingId, cart)) return cart;

        return [...cart, listingId];
      };
      const newCart = getMergedCart();
      if (cartBookingSearchId === bookingSearchId && length(newCart) === length(cart)) {
        dispatch(addToCartSuccess());
        return;
      }

      await updateCart(dispatch, getState, sdk, { newCart, params, action: CART_ADDING_SUCCESS });
      dispatch(markDatadogRumCustomerLeadAddToCartEligible());
      dispatch(addToCartReInit());
    } catch (e) {
      log.error(e, 'addToCart-failed');
      dispatch(updateCartError(storableError(e)));
    }
  };

export const removeFromCart = (listingId) => async (dispatch, getState, sdk) => {
  try {
    dispatch(removeFromCartRequest([listingId]));
    const cart = pathOr(
      [],
      ['currentUser', 'attributes', 'profile', 'publicData', 'addToCart'],
      getState().user
    );
    const newCart = cart.filter((id) => id !== listingId);

    await updateCart(dispatch, getState, sdk, {
      newCart,
      params: null,
      action: CART_UPDATE_SUCCESS,
    });
  } catch (e) {
    log.error(e, 'removeFromCart-failed');
    dispatch(updateCartError(storableError(e)));
  }
};

const updateCart = async (dispatch, getState, sdk, { newCart, params, action }) => {
  try {
    const getPrivateDataUpdatedMaybe = (params) => {
      if (!params) return {};

      const { bookingSearchId: cartBookingSearchId, bookingParams: cartBookingParams } = params;
      return {
        privateData: {
          cartBookingSearchId,
          cartBookingParams,
          cartLastUpdatedAt: moment().toISOString(),
        },
      };
    };
    const payloadUpdated = {
      publicData: {
        addToCart: newCart,
      },
      ...getPrivateDataUpdatedMaybe(params),
    };
    await dispatch(updateProfile(payloadUpdated));

    dispatch({
      type: action ? action : CART_UPDATE_SUCCESS,
    });
  } catch (e) {
    log.error(e, 'updateCart-failed');
    dispatch(updateCartError(storableError(e)));
  }
};

export const refreshCartMaybe =
  (bookingSearchId, bookingParams, forceReset = false) =>
  async (dispatch, getState, sdk) => {
    try {
      const isAuthenticated = pathOr(false, ['isAuthenticated'], getState().Auth);
      const currentUser = getState().user?.currentUser;
      if (!isAuthenticated || !currentUser) return;

      const cartBookingSearchId = pathOr(
        null,
        ['currentUser', 'attributes', 'profile', 'privateData', 'cartBookingSearchId'],
        getState().user
      );
      const cartLastUpdatedAt = pathOr(
        null,
        ['currentUser', 'attributes', 'profile', 'privateData', 'cartLastUpdatedAt'],
        getState().user
      );
      const getHasExpiredCart = () => {
        if (!cartLastUpdatedAt) return false;

        const cartLastUpdatedAtDate = moment(cartLastUpdatedAt);
        const cartExpireDate = moment(cartLastUpdatedAt).add(60, 'minutes');
        return cartLastUpdatedAtDate.isAfter(cartExpireDate);
      };
      const hasExpiredCart = getHasExpiredCart();
      if (!forceReset && cartBookingSearchId === bookingSearchId && !hasExpiredCart) return;

      await updateCart(dispatch, getState, sdk, {
        newCart: [],
        params: bookingParams,
        action: CART_ADDING_SUCCESS,
      });
      dispatch(addToCartReInit());
    } catch (e) {
      log.error(e, 'refreshCartMaybe-failed');
      dispatch(updateCartError(storableError(e)));
    }
  };
